import { IconType } from 'react-icons';
import { MdOutlineAssignmentInd, MdYoutubeSearchedFor, MdPayment } from 'react-icons/md';

export type TierPosition = 1 | 2 | 3;

export const AFFILIATE_TIER_NAME = 'affiliate';
export const CURATOR_TIER_NAME = 'Curator';

export const isNewAdminDashboardTier = (tierName?: string | null): boolean => {
  if (!tierName) return false;
  return tierName === AFFILIATE_TIER_NAME || tierName === CURATOR_TIER_NAME;
};

export const FEATURE_IDS = {
  NUMBER_OF_CATEGORIES: 'number_of_categories',
  ENABLED_CATEGORY_TYPES: 'enabled_category_types',
  LISTING_IMPORTS: 'listing_imports',
  SOCIAL_AUTOMATION: 'social_automation',
  CUSTOM_DOMAIN: 'custom_domain',
  SINGLE_LISTING_IMPORT: 'single_listing_import',
  STRIPE_CONNECT: 'stripe_connect',
} as const;

export type FeatureId = (typeof FEATURE_IDS)[keyof typeof FEATURE_IDS];

export const getFeaturePosition = (featureId: FeatureId): number => {
  const featureIdsArray = Object.values(FEATURE_IDS);
  return featureIdsArray.indexOf(featureId);
};

export const getFeatureIdByPosition = (position: number): FeatureId | undefined => {
  const featureIdsArray = Object.values(FEATURE_IDS);
  return featureIdsArray[position] as FeatureId | undefined;
};

export interface UpsellFeature {
  id: FeatureId;
  displayName: string;
  description: string;
  minimumTierPosition: TierPosition;
  tierLink: string;
  icon?: IconType;
}

export const UPSELL_FEATURES: Record<FeatureId, UpsellFeature> = {
  [FEATURE_IDS.LISTING_IMPORTS]: {
    id: FEATURE_IDS.LISTING_IMPORTS,
    displayName: 'Automated Listing Imports',
    description: 'Save time by using our AI to pull in relevant listings from across the web.',
    minimumTierPosition: 3,
    tierLink: '/pricing/pro',
    icon: MdYoutubeSearchedFor,
  },
  [FEATURE_IDS.SOCIAL_AUTOMATION]: {
    id: FEATURE_IDS.SOCIAL_AUTOMATION,
    displayName: 'Social Media Automation',
    description:
      'Connect your social media account(s) to automate posts and stories that drive usage from your audience.',
    minimumTierPosition: 1,
    tierLink: '/pricing/basic',
    icon: MdOutlineAssignmentInd,
  },
  [FEATURE_IDS.CUSTOM_DOMAIN]: {
    id: FEATURE_IDS.CUSTOM_DOMAIN,
    displayName: 'Custom Domain',
    description:
      'Make your site truly yours with a custom domain that matches your brand, builds trust with customers, and improves your online presence.',
    minimumTierPosition: 1,
    tierLink: '/pricing/basic',
  },
  [FEATURE_IDS.NUMBER_OF_CATEGORIES]: {
    id: FEATURE_IDS.NUMBER_OF_CATEGORIES,
    displayName: 'Category Creation',
    description: 'Create and manage more categories to organize your listings.',
    minimumTierPosition: 1,
    tierLink: '/pricing/basic',
  },
  [FEATURE_IDS.ENABLED_CATEGORY_TYPES]: {
    id: FEATURE_IDS.ENABLED_CATEGORY_TYPES,
    displayName: 'All Category Types',
    description:
      'Create and manage more category types to increase your reach with a more flexible site.',
    minimumTierPosition: 1,
    tierLink: '/pricing/basic',
  },
  [FEATURE_IDS.SINGLE_LISTING_IMPORT]: {
    id: FEATURE_IDS.SINGLE_LISTING_IMPORT,
    displayName: 'Single Listing Import',
    description:
      'Import a single listing from a platform such as Craigslist, Facebook Marketplace, and more.',
    minimumTierPosition: 1,
    tierLink: '/pricing/basic',
  },
  [FEATURE_IDS.STRIPE_CONNECT]: {
    id: FEATURE_IDS.STRIPE_CONNECT,
    displayName: 'Stripe Connect',
    description:
      'Accept payments directly to your Stripe account with lower fees and faster payouts.',
    minimumTierPosition: 1,
    tierLink: '/pricing/basic',
    icon: MdPayment,
  },
};
